import { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { SectorPicker } from "@/components/SectorPicker";
import { scanAccept, scanRefuse } from "@/lib/scanFeedback";
import { toast } from "sonner";
import { MapPin, Loader2, AlertTriangle, Undo2, ArrowRightLeft } from "lucide-react";

// PUTTING A TRUCK SOMEWHERE — one truck or thirty, same three beats: pick the sector, read back
// what is about to happen in plain English, commit.
//
// Three rules it holds, all his:
//   · A TRUCK THAT ALREADY HAS A LOCATION IS NEVER MOVED SILENTLY. "If I bulk-select and three of
//     them are already placed, does it overwrite silently? It shouldn't." They are named before
//     and after, left alone by default, and moving them is a separate deliberate press.
//   · A SKIPPED TRUCK MUST BE FINDABLE, NOT JUST COUNTED. "The real question is: were those 6 in
//     the right place, or do I actually want to move them?" So the list says which ones and where
//     they are, and "move them anyway" is right there — place and move are different intentions.
//   · ONE PRESS BACK. A wrong sector is one Undo, not thirty corrections — the server records
//     every placement as a batch and the toast carries the undo.
const words = (dest) => (dest?.name && dest.name !== dest.short_code
  ? `${dest.name} (${dest.short_code})` : (dest?.short_code || dest?.name || "that sector"));

export function undoPlacementToast(batchId, count, onDone, message) {
  if (!batchId) return;
  toast.success(message || `${count} truck${count === 1 ? "" : "s"} placed`, {
    duration: 12000,
    action: {
      label: "Undo",
      onClick: async () => {
        try {
          const { data } = await api.post(`/donors/place-batch/${batchId}/undo`);
          data.ok ? toast.success(data.message) : toast.error(data.message);
          onDone?.();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
      },
    },
  });
}

export const PlaceInSectorDialog = ({ donorId, stockNumber, donorIds, onDone, onPlaced, trigger }) => {
  const [open, setOpen] = useState(false);
  const [picked, setPicked] = useState(null);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);      // what happened, kept on screen when some were skipped
  const [scope, setScope] = useState(null);        // the "move those ones anyway" second action
  const [movePlaced, setMovePlaced] = useState(false);
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);

  const bulk = Array.isArray(donorIds);
  const ids = scope || donorIds || [];
  const url = bulk ? `/donors/place-bulk` : `/donors/${donorId}/place`;
  const code = picked?.code || "";

  const reset = () => { setPicked(null); setPreview(null); setResult(null); setScope(null); setMovePlaced(false); setBusy(false); };
  // THE REFRESH HAPPENS WHEN THIS CLOSES, NEVER MID-JOB. Refreshing the page behind re-renders the
  // row that owns this dialog's open flag — that is how the presence dialog once tore itself down
  // mid-save, and a bulk refresh also clears the selection this dialog is standing on.
  const shut = () => { setOpen(false); const had = dirty; reset(); setDirty(false); if (had) onDone?.(); };

  const check = async (sector, move = movePlaced, only = scope) => {
    setBusy(true);
    try {
      const { data } = await api.post(url, bulk
        ? { donor_ids: only || donorIds, location_scan: sector.code, confirm: false, move_placed: move }
        : { location_scan: sector.code, confirm: false });
      setResult(null);
      if (data.needs_confirm) setPreview(data);
      else { scanRefuse(data.message || "That code didn't match a sector."); toast.error(data.message || "That code didn't match a sector."); setPreview(data.already_placed ? data : null); }
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const commit = async () => {
    setBusy(true);
    const skippedRows = preview?.already_placed || [];
    try {
      const { data } = await api.post(url, bulk
        ? { donor_ids: ids, location_scan: code, confirm: true, move_placed: movePlaced }
        : { location_scan: code, confirm: true });
      if (data.ok) {
        scanAccept(`PLACED IN ${data.destination?.short_code || code}`);
        setDirty(true);
        onPlaced?.({ code: data.destination?.short_code || code, name: data.destination?.name || code });
        if (data.batch_id) undoPlacementToast(data.batch_id, data.count ?? 1, () => { setOpen(false); reset(); onDone?.(); }, data.message);
        else toast.success(data.message);
        if (data.destination?.holding) {
          toast.message(`${words(data.destination)} is a holding area — the truck counts as placed and is marked "parked, not put away".`);
        }
        // SOME WERE LEFT ALONE: stay open and say WHICH, because a count he can't open doesn't
        // answer the only question that matters about them.
        if (bulk && (data.skipped || 0) > 0 && skippedRows.length) {
          setPreview(null);
          setResult({ ...data, left: skippedRows, dest: data.destination });
          setBusy(false);
          return;
        }
        shut();
      } else { scanRefuse(data.message || "Placement failed."); toast.error(data.message || "Placement failed."); }
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const moveThoseAnyway = () => {
    const left = (result?.left || []).map((t) => t.id);
    setScope(left);
    setMovePlaced(true);
    setResult(null);
    if (picked) check(picked, true, left);
  };

  const n = bulk ? ids.length : 1;
  const title = bulk ? `Place ${n} truck${n === 1 ? "" : "s"} in a sector` : `Place ${stockNumber || "truck"} in a sector`;

  return (
    <>
      {trigger
        ? <span onClick={() => { reset(); setOpen(true); }}>{trigger}</span>
        : <Button data-testid="donor-place-sector-btn" variant="outline" onClick={() => { reset(); setOpen(true); }}
            className="rounded-lg border-slate-300 h-9"><MapPin className="w-4 h-4 mr-1.5" />Place in sector</Button>}
      <Dialog open={open} onOpenChange={(o) => { if (!o) shut(); else setOpen(true); }}>
        <DialogContent className="rounded-xl max-w-lg max-h-kb overflow-y-auto">
          <DialogHeader>
            <DialogTitle data-testid="donor-place-title">{scope ? `Move ${n} truck${n === 1 ? "" : "s"} that already had a location` : title}</DialogTitle>
            <DialogDescription>
              Tap one of your sectors — most recently used first — or scan/type any location code.
              You'll read back what's about to happen before it's placed.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <SectorPicker testid="donor-place" picked={picked} big
              onPick={(s) => { setPicked(s); setPreview(null); check(s); }} />

            {preview?.already_placed?.length > 0 && (
              <div data-testid="donor-place-already" className="rounded-lg border-2 border-amber-300 bg-amber-50 p-2.5 space-y-1.5">
                <p className="text-xs font-bold text-amber-900 flex items-center gap-1.5">
                  <AlertTriangle className="w-3.5 h-3.5" />
                  {preview.already_placed.length} of these already {preview.already_placed.length === 1 ? "has" : "have"} a location
                </p>
                <p className="text-[11px] text-amber-800">
                  {preview.already_placed.slice(0, 6).map((t) => `${t.stock_number} (${t.location_label || "somewhere"})`).join(" · ")}
                  {preview.already_placed.length > 6 ? ` … +${preview.already_placed.length - 6} more` : ""}
                </p>
                <label className="flex items-center gap-2 text-[11px] font-semibold text-amber-900 cursor-pointer">
                  <input type="checkbox" data-testid="donor-place-move-placed" checked={movePlaced}
                    onChange={(e) => { setMovePlaced(e.target.checked); if (picked) check(picked, e.target.checked); }}
                    className="w-4 h-4 accent-[#E0781A]" />
                  Move {preview.already_placed.length === 1 ? "it" : "them"} too — out of where {preview.already_placed.length === 1 ? "it is" : "they are"} now
                </label>
                {!movePlaced && <p className="text-[10px] text-amber-700">Left unticked, {preview.already_placed.length === 1 ? "it stays" : "they stay"} where {preview.already_placed.length === 1 ? "it is" : "they are"}.</p>}
              </div>
            )}

            {preview?.prompt && (
              <div data-testid="donor-place-confirm" className="rounded-lg border-2 border-[#E0781A] bg-orange-50 p-3">
                <p className="font-semibold text-[#C76714] text-sm">{preview.prompt}</p>
                {preview.destination?.holding && (
                  <p className="text-xs text-amber-700 mt-1">
                    This is a holding area — the truck counts as PLACED and is marked "parked, not put away".
                  </p>
                )}
                {preview.count_already_here > 0 && (
                  <p className="text-[11px] text-slate-600 mt-1">{preview.count_already_here} already there — untouched.</p>
                )}
              </div>
            )}
            {preview && !preview.needs_confirm && preview.message && (
              <p data-testid="donor-place-nothing" className="text-xs text-[#B01E28]">{preview.message}</p>
            )}

            {result && (
              <div data-testid="donor-place-result" className="rounded-lg border-2 border-emerald-300 bg-emerald-50 p-3 space-y-2">
                <p className="text-sm font-semibold text-emerald-900">{result.message}</p>
                <p className="text-[11px] font-bold text-slate-700">
                  Left alone — {result.left.length === 1 ? "it already had a location" : "they already had a location"}:
                </p>
                <ul data-testid="donor-place-left-alone" className="max-h-40 overflow-y-auto space-y-0.5">
                  {result.left.map((t) => (
                    <li key={t.id} className="flex items-center justify-between gap-2 text-[11px] text-slate-700 border-b border-emerald-200/60 py-0.5">
                      <span className="font-mono font-bold">{t.stock_number}</span>
                      <span className="truncate text-slate-500">{t.location_label || "somewhere"}</span>
                    </li>
                  ))}
                </ul>
                <Button data-testid="donor-place-move-anyway" size="sm" variant="outline" onClick={moveThoseAnyway}
                  className="rounded-lg border-[#E0781A] text-[#C76714] h-9 w-full">
                  <ArrowRightLeft className="w-3.5 h-3.5 mr-1.5" />
                  Move {result.left.length === 1 ? "it" : `those ${result.left.length}`} into {words(result.dest)} anyway
                </Button>
                <p className="text-[10px] text-slate-500">
                  Consolidating a row or clearing a sector is a different job from placing — so it's
                  a second, deliberate press, and it's undoable too.
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            {result ? (
              <Button data-testid="donor-place-done-btn" onClick={shut} className="rounded-lg bg-slate-800 hover:bg-slate-700 h-11">Done</Button>
            ) : (
              <Button data-testid="donor-place-confirm-btn" onClick={commit}
                disabled={busy || !preview?.needs_confirm}
                className="rounded-lg bg-[#E0781A] hover:bg-[#C76714] h-11">
                {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <MapPin className="w-4 h-4 mr-1.5" />}
                {preview?.needs_confirm
                  ? (scope ? (n === 1 ? "Yes — move it" : "Yes — move them")
                           : (bulk && n !== 1 ? "Yes — place them" : "Yes — place it"))
                  : "Pick a sector first"}
              </Button>
            )}
          </DialogFooter>
          <p className="text-[10px] text-slate-400 flex items-center gap-1"><Undo2 className="w-3 h-3" />Every placement can be put back in one press.</p>
        </DialogContent>
      </Dialog>
    </>
  );
};
